'use client';

import { motion } from 'framer-motion';
import {
    Brain, Sparkles, TrendingUp, MessageSquare, Zap,
    BarChart3, MapPin, Clock, Bot, ArrowRight, CheckCircle2,
    LineChart, Target, Shield,
} from 'lucide-react';
import { EASE_OUT, EASE_CUSTOM } from '@/lib/animations'

const fadeUp = {
    hidden: { opacity: 0, y: 24 },
    show: (i: number) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.08, ease: EASE_CUSTOM } }),
};

/* ── AI Feature cards ── */
const AI_FEATURES = [
    {
        icon: MessageSquare,
        title: 'Asistentes inteligentes',
        desc: 'Chatbots con contexto real de tu negocio que responden, reservan y cualifican leads 24/7.',
        color: 'blue',
    },
    {
        icon: TrendingUp,
        title: 'Predicciones y estimaciones',
        desc: 'Modelos que estiman demanda, facturación y comportamiento de clientes por zona y temporada.',
        color: 'indigo',
    },
    {
        icon: Zap,
        title: 'Automatización con IA',
        desc: 'Flujos que clasifican emails, extraen datos de facturas, generan informes y priorizan tareas sin intervención.',
        color: 'violet',
    },
    {
        icon: BarChart3,
        title: 'Dashboards predictivos',
        desc: 'Paneles que no solo muestran datos — te dicen qué va a pasar y qué decisiones tomar.',
        color: 'sky',
    },
    {
        icon: MapPin,
        title: 'Análisis geoespacial',
        desc: 'Estimaciones por distrito, código postal o zona: potencial de mercado, densidad de competencia, oportunidades.',
        color: 'emerald',
    },
    {
        icon: Shield,
        title: 'IA privada y segura',
        desc: 'Tus datos nunca salen de tu infraestructura. Modelos desplegados en tu entorno, sin dependencias externas.',
        color: 'amber',
    },
];

const colorMap = {
    blue:    { bg: 'bg-blue-50',    border: 'border-blue-100',    icon: 'text-blue-600',    dot: 'bg-blue-500' },
    indigo:  { bg: 'bg-indigo-50',  border: 'border-indigo-100',  icon: 'text-indigo-600',  dot: 'bg-indigo-500' },
    violet:  { bg: 'bg-violet-50',  border: 'border-violet-100',  icon: 'text-violet-600',  dot: 'bg-violet-500' },
    sky:     { bg: 'bg-sky-50',     border: 'border-sky-100',     icon: 'text-sky-600',     dot: 'bg-sky-500' },
    emerald: { bg: 'bg-emerald-50', border: 'border-emerald-100', icon: 'text-emerald-600', dot: 'bg-emerald-500' },
    amber:   { bg: 'bg-amber-50',   border: 'border-amber-100',   icon: 'text-amber-600',   dot: 'bg-amber-500' },
};

/* ── AI Dashboard Mockup ── */
function AIDashboardMockup() {
    const districts = [
        { name: 'Salamanca',    pct: 87, color: 'bg-blue-500' },
        { name: 'Chamberí',     pct: 74, color: 'bg-indigo-500' },
        { name: 'Retiro',       pct: 68, color: 'bg-violet-500' },
        { name: 'Chamartín',    pct: 61, color: 'bg-sky-500' },
        { name: 'Centro',       pct: 55, color: 'bg-emerald-500' },
    ];

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: EASE_OUT }}
            className="rounded-2xl overflow-hidden shadow-[0_24px_60px_rgba(99,102,241,0.14),0_6px_20px_rgba(15,23,42,0.08)]"
        >
            {/* Browser chrome */}
            <div className="flex items-center gap-1.5 px-4 py-3 bg-white/90 border-b border-slate-200/60 backdrop-blur-xl">
                <span className="w-2.5 h-2.5 rounded-full bg-red-400/70" />
                <span className="w-2.5 h-2.5 rounded-full bg-amber-400/70" />
                <span className="w-2.5 h-2.5 rounded-full bg-green-400/70" />
                <div className="ml-3 flex-1 h-5 rounded-md bg-slate-100 border border-slate-200/60 flex items-center px-2 gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
                    <span className="text-[9px] text-slate-400 font-mono">ai.tuempresa.com/dashboard</span>
                </div>
            </div>

            <div className="bg-white/90 backdrop-blur-xl p-5 space-y-4">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Brain className="w-4 h-4 text-indigo-600" />
                        <span className="text-[11px] font-semibold text-slate-700">Panel IA — Predicciones</span>
                    </div>
                    <span className="flex items-center gap-1.5 text-[9px] font-mono text-indigo-600 bg-indigo-50 border border-indigo-200/60 px-2 py-0.5 rounded-full">
                        <Sparkles className="w-2.5 h-2.5" />
                        IA activa
                    </span>
                </div>

                {/* AI Chat suggestion */}
                <div className="bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-100 rounded-xl p-3">
                    <div className="flex items-start gap-2.5">
                        <div className="w-6 h-6 rounded-lg bg-indigo-100 flex items-center justify-center shrink-0 mt-0.5">
                            <Bot className="w-3 h-3 text-indigo-600" />
                        </div>
                        <div>
                            <p className="text-[10px] font-semibold text-indigo-800">Sugerencia de IA</p>
                            <p className="text-[9px] text-indigo-600/80 mt-0.5 leading-relaxed">
                                Los martes entre 14-16h tienes un 43% menos de reservas. Recomienda enviar una promo automática a los 127 clientes inactivos de esa franja.
                            </p>
                            <div className="flex gap-2 mt-2">
                                <span className="text-[8px] font-semibold text-white bg-indigo-600 px-2 py-0.5 rounded-md">Aplicar ahora</span>
                                <span className="text-[8px] font-medium text-indigo-500 border border-indigo-200 px-2 py-0.5 rounded-md">Ver detalles</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* District estimation */}
                <div className="bg-slate-50/80 border border-slate-100 rounded-xl p-3">
                    <div className="flex items-center justify-between mb-2.5">
                        <span className="text-[9px] font-semibold text-slate-600 flex items-center gap-1.5">
                            <Target className="w-3 h-3 text-slate-400" />
                            Potencial por distrito — Madrid
                        </span>
                        <span className="text-[8px] text-slate-400 font-mono">Modelo v2.4</span>
                    </div>
                    <div className="space-y-2">
                        {districts.map((d, i) => (
                            <motion.div
                                key={d.name}
                                initial={{ opacity: 0, x: -10 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.3 + i * 0.08 }}
                                className="flex items-center gap-2"
                            >
                                <span className="text-[9px] text-slate-500 w-16 shrink-0">{d.name}</span>
                                <div className="flex-1 h-2 bg-slate-200/60 rounded-full overflow-hidden">
                                    <motion.div
                                        initial={{ width: 0 }}
                                        whileInView={{ width: `${d.pct}%` }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.8, delay: 0.4 + i * 0.1, ease: 'easeOut' as const }}
                                        className={`h-full rounded-full ${d.color}`}
                                    />
                                </div>
                                <span className="text-[9px] font-semibold text-slate-700 w-8 text-right">{d.pct}%</span>
                            </motion.div>
                        ))}
                    </div>
                </div>

                {/* Bottom row: predictions */}
                <div className="grid grid-cols-3 gap-2">
                    {[
                        { icon: TrendingUp, label: 'Próx. mes', val: '+23%', sub: 'facturación', color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-100' },
                        { icon: Clock, label: 'Ahorro IA', val: '14h/sem', sub: 'automatizado', color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-100' },
                        { icon: LineChart, label: 'Precisión', val: '94.2%', sub: 'predicciones', color: 'text-indigo-600', bg: 'bg-indigo-50', border: 'border-indigo-100' },
                    ].map((m, i) => {
                        const Icon = m.icon;
                        return (
                            <motion.div
                                key={m.label}
                                initial={{ opacity: 0, y: 8 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.5 + i * 0.1, duration: 0.5 }}
                                className={`${m.bg} border ${m.border} rounded-xl p-2.5 text-center`}
                            >
                                <Icon className={`w-3 h-3 mx-auto mb-1 ${m.color}`} />
                                <div className={`text-sm font-bold ${m.color} leading-none`}>{m.val}</div>
                                <div className="text-[8px] text-slate-400 mt-0.5">{m.sub}</div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </motion.div>
    );
}

export default function SoftwareIA() {
    return (
        <section id="software-ia" className="py-20 md:py-32 relative z-10">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">

                {/* Header */}
                <div className="mb-16 max-w-3xl">
                    <motion.div custom={0} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
                        className="inline-flex items-center gap-2 rounded-full border border-indigo-200/60 bg-indigo-50/70 px-4 py-1.5 backdrop-blur-sm mb-6"
                    >
                        <Sparkles className="w-3.5 h-3.5 text-indigo-600" />
                        <span className="text-xs font-semibold tracking-wide text-indigo-700">Nueva generación</span>
                    </motion.div>

                    <motion.h2 custom={1} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
                        className="text-2xl sm:text-4xl md:text-5xl font-black tracking-tight text-slate-900 mb-4">
                        Software + IA.
                        <br />
                        <span className="text-blue-600">
                            Inteligencia real para tu negocio.
                        </span>
                    </motion.h2>
                    <motion.p custom={2} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
                        className="text-lg text-slate-500 font-light leading-relaxed">
                        No es solo software. Integramos inteligencia artificial directamente en tus herramientas para que tu negocio tome mejores decisiones, ahorre tiempo y descubra oportunidades que antes eran invisibles.
                    </motion.p>
                </div>

                {/* Main grid: mockup + features */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">

                    {/* Left: AI Dashboard mockup */}
                    <div className="relative">
                        {/* Glows */}
                        <div className="absolute -inset-4 -z-10 bg-indigo-200/20 rounded-3xl blur-[50px]" />
                        <AIDashboardMockup />
                    </div>

                    {/* Right: Feature cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {AI_FEATURES.map((f, i) => {
                            const c = colorMap[f.color as keyof typeof colorMap];
                            const Icon = f.icon;
                            return (
                                <motion.div
                                    key={f.title}
                                    custom={i + 3}
                                    variants={fadeUp}
                                    initial="hidden"
                                    whileInView="show"
                                    viewport={{ once: true, margin: '-40px' }}
                                    className="glass-card rounded-2xl p-5 group cursor-default hover:shadow-lg transition-shadow duration-300"
                                >
                                    <div className={`w-9 h-9 rounded-xl ${c.bg} border ${c.border} flex items-center justify-center mb-3`}>
                                        <Icon className={`w-4 h-4 ${c.icon}`} />
                                    </div>
                                    <h3 className="text-sm font-bold text-slate-900 mb-1.5">{f.title}</h3>
                                    <p className="text-xs text-slate-500 leading-relaxed">{f.desc}</p>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>

                {/* Bottom CTA */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7, delay: 0.3 }}
                    className="mt-14 flex flex-col sm:flex-row items-center justify-center gap-4"
                >
                    <a
                        href="https://calendly.com/innovatheratech/30min"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-gradient-to-r from-indigo-600 to-blue-600 text-white text-sm font-semibold shadow-[0_8px_30px_rgba(99,102,241,0.35)] hover:shadow-[0_12px_40px_rgba(99,102,241,0.45)] hover:-translate-y-0.5 active:scale-95 transition-all"
                    >
                        <Brain className="w-4 h-4" />
                        Descubre cómo la IA puede ayudarte
                        <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </a>
                    <span className="text-xs text-slate-400 font-mono uppercase tracking-widest">
                        Diagnóstico gratuito · 30 min
                    </span>
                </motion.div>
            </div>
        </section>
    );
}
